import { useState } from "react";
import { ExternalLink, ChevronDown, ChevronRight, Scale, Landmark, Building2, FileCog, Info } from "lucide-react";

export const UNAVAILABLE = "Source detail not available — independent verification required.";

const TYPE_STYLE = {
  "Federal law or regulation": { icon: Scale, cls: "bg-[#0A2A5E] text-white" },
  "Federal-sector policy or procedure": { icon: Landmark, cls: "bg-[#1E5AB8] text-white" },
  "Official government guidance": { icon: Landmark, cls: "bg-[#DBEAFE] text-[#0A2A5E]" },
  "State or local authority": { icon: Landmark, cls: "bg-violet-100 text-violet-900" },
  "Organizational policy": { icon: Building2, cls: "bg-[#FDECD0] text-[#7C3F00]" },
  "Internal procedure or SOP": { icon: FileCog, cls: "bg-emerald-100 text-emerald-800" },
  "General best-practice guidance": { icon: Info, cls: "bg-slate-200 text-slate-700" },
  "Proprietary LUMIS AI™ educational resource": { icon: Info, cls: "bg-[#F5A623] text-[#0A2A5E]" },
};

export function ApplicabilityNote({ text, testid }) {
  if (!text) return <span data-testid={testid} className="text-xs italic text-slate-400">{UNAVAILABLE}</span>;
  const fed = /federal-sector/i.test(text);
  return <span data-testid={testid} className={`text-xs leading-relaxed ${fed ? "text-[#7C3F00] font-semibold" : "text-slate-700"}`}>{fed ? "⚠ " : ""}{text}</span>;
}

export function SourceTypeBadge({ type, testid }) {
  const t = TYPE_STYLE[type] || { icon: Info, cls: "bg-slate-100 text-slate-500" };
  const Icon = t.icon;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${t.cls}`}>
      <Icon className="w-3 h-3" /> {type || "Type not specified"}
    </span>
  );
}

function Field({ label, value, tid }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</dt>
      <dd data-testid={tid} className={`text-xs mt-0.5 ${value ? "text-slate-800" : "text-slate-400 italic"}`}>{value || UNAVAILABLE}</dd>
    </div>
  );
}

export function SourceDetailCard({ s, onOpen }) {
  return (
    <div data-testid={`source-detail-${s.doc_id}`} className="rounded-lg border border-[#FBD38D] bg-white/70 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => onOpen?.(s.doc_id)} className="text-sm font-semibold text-[#0A2A5E] hover:underline text-left">{s.title}</button>
        <SourceTypeBadge type={s.source_type} testid={`source-type-${s.doc_id}`} />
        {s.jurisdiction && <span data-testid={`source-jurisdiction-${s.doc_id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-slate-300 text-slate-600">{s.jurisdiction}</span>}
        {s.status && <span data-testid={`source-status-${s.doc_id}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${s.status === "Active" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{s.status}</span>}
      </div>
      <div className="mt-2"><span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block">Applicability</span><ApplicabilityNote text={s.applicability} testid={`source-applicability-${s.doc_id}`} /></div>
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mt-2">
        <Field label="Issuing agency / organization" value={s.agency} tid={`source-agency-${s.doc_id}`} />
        <Field label="Section / regulation / page" value={s.section || s.citation} tid={`source-section-${s.doc_id}`} />
        <Field label="Jurisdiction" value={s.jurisdiction} />
        <Field label="Publication / revision date" value={s.published} tid={`source-published-${s.doc_id}`} />
        <Field label="Last verified" value={s.last_verified} tid={`source-verified-${s.doc_id}`} />
        <Field label="Document version" value={s.version ? `v${s.version}` : null} />
      </dl>
      <div className="mt-2">
        {s.source_url ? (
          <a href={s.source_url} target="_blank" rel="noreferrer" data-testid={`source-link-${s.doc_id}`} className="text-xs text-[#0A2A5E] font-semibold hover:underline inline-flex items-center gap-1">Open source <ExternalLink className="w-3 h-3" /></a>
        ) : <span className="text-xs text-slate-400 italic">{UNAVAILABLE}</span>}
      </div>
    </div>
  );
}

export default function SourceDetails({ sources, onOpen }) {
  const [open, setOpen] = useState(false);
  if (!sources?.length) return null;
  const types = [...new Set(sources.map((s) => s.source_type).filter(Boolean))];
  return (
    <div className="mt-3 pt-3 border-t border-[#F5A623]">
      <button data-testid="source-details-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#7C3F00] hover:underline">
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Source details ({sources.length})
        {types.length > 1 && <span className="normal-case tracking-normal font-normal text-slate-500 ml-1">· mixes {types.length} source types — do not combine</span>}
      </button>
      {open && <div data-testid="source-details" className="mt-2 space-y-2">{sources.map((s) => <SourceDetailCard key={s.doc_id} s={s} onOpen={onOpen} />)}</div>}
    </div>
  );
}
